import { useState } from 'react';
import './UploadFiles.css';
import uploadFileIcon from './images/upload-file.svg';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ClipLoader } from 'react-spinners';
import ImageModal from '../../../citizenreports/ImageModal';
import DeleteWorkOrderFile from './DeleteWorkOrderFile';
import FileInformationCard from './FileInformationCard';

export default function ClosedWorkOrderUploadedFiles({
  problemId,
  workOrder,
  hideModal,
}) {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [firstRender, setFirstRender] = useState(true);

  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
  const token = localStorage.getItem('token');

  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const apiUrl = process.env.REACT_APP_BACKEND_API;

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files).filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        return false;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        return false;
      }
      return true;
    });

    if (e.target.files) {
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const fetchWorkOrderFiles = async () => {
    const response = await fetch(`${apiUrl}/work-order-files/${workOrder.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  };

  const { data: workOrderFiles, refetch } = useQuery(
    ['workOrderFiles'],
    fetchWorkOrderFiles,
    {
      enabled: firstRender,
      onSuccess: (response) => {
        setIsLoading(false);
        setFirstRender(false);
      },
      onError: (error) => {},
    }
  );

  const fetchWorkOrderDetails = async () => {
    const response = await fetch(`${apiUrl}/work-orders/${problemId}/problem`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  };

  const { data: workOrderDetails } = useQuery(
    ['workOrderDetails'],
    fetchWorkOrderDetails,
    {
      enabled: firstRender,
      onSuccess: (response) => {
        console.log(response);
      },
      onError: (error) => {},
    }
  );

  const handleFileRemove = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleDisplayName = (fileName) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) return fileName;
    return fileName.substring(0, lastDotIndex);
  };

  const handleFolderName = (folderName) => {
    const index = folderName.length - 3;

    return folderName.slice(0, index) + '_' + folderName.slice(index + 1);
  };

  const onSubmit = () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append(
      'folder',
      `radninalozi/${handleFolderName(workOrder.serialNumber)}`
    );
    [...files].forEach((file) => {
      formData.append('documents', file);
    });

    uploadWorkOrderFilesMutate(formData);
  };

  const { mutate: uploadWorkOrderFilesMutate } = useMutation({
    mutationFn: (problemData) =>
      fetch(`${apiUrl}/upload/documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: problemData,
      }).then((res) => res.json()),

    onSuccess: (response) => {
      if (
        (response.statusCode < 300 && response.statusCode > 199) ||
        !response.statusCode
      ) {
        setFiles([]);
        const filesFormat = {
          workOrder: workOrderDetails,
          files: response.documents.map((file) => {
            return {
              ...file,
              folder: handleFolderName(workOrder.serialNumber),
              extension: file.type,
            };
          }),
        };
        saveFilesMutate(filesFormat);
      }
    },
    onError: () => {
      console.log('error');
      setIsLoading(false);
    },
  });

  const { mutate: saveFilesMutate } = useMutation({
    mutationFn: (saveFilesData) =>
      fetch(`${apiUrl}/work-order-files`, {
        method: 'POST',
        body: JSON.stringify({ ...saveFilesData }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }).then((res) => res.json()),

    onSuccess: (response) => {
      if (
        (response.statusCode < 300 && response.statusCode > 199) ||
        !response.statusCode
      ) {
        refetch();
      }
    },
    onError: () => {
      console.log('error');
      setIsLoading(false);
    },
  });

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const openImageModal = (imageDetails) => {
    setSelectedImage(imageDetails);
    setShowImageModal(true);
  };

  const handleShowDeleteFileModal = (e, fileId) => {
    e.stopPropagation();

    setSelectedFile(fileId);
    setShowDeleteFileModal(true);
  };

  const closeShowDeleteFileModal = () => {
    setShowDeleteFileModal(false);
  };

  return (
    <div>
      {showDeleteFileModal && (
        <DeleteWorkOrderFile
          workOrderFileId={selectedFile}
          closeModal={closeShowDeleteFileModal}
          refetchFiles={refetch}
        />
      )}
      {!isLoading && workOrderFiles.files.length > 0 && showImageModal && (
        <ImageModal
          title={handleDisplayName(selectedImage.originalName)}
          folder={`radninalozi/${selectedImage.folder}`}
          image={selectedImage.newName}
          closeModal={closeImageModal}
        />
      )}

      {isLoading && (
        <div className="upload-files-spinner">
          <ClipLoader
            color={'gray'}
            loading={isLoading}
            size={60}
            data-testid="loader"
          />
        </div>
      )}
      {!isLoading && (
        <div className="upload-files-modal">
          <input
            type="file"
            id="upload-files"
            accept=".jpg,.jpeg,.png,.pdf"
            multiple
            onChange={handleFileChange}
            hidden
          />

          {workOrderFiles.files.length > 0 && (
            <FileInformationCard
              workOrderFiles={workOrderFiles.files}
              renderType={'uploaded'}
              openImageModal={openImageModal}
              handleFileRemove={handleFileRemove}
              handleDisplayName={handleDisplayName}
              handleShowDeleteFileModal={handleShowDeleteFileModal}
            />
          )}

          <FileInformationCard
            workOrderFiles={files}
            renderType={'added'}
            openImageModal={openImageModal}
            handleFileRemove={handleFileRemove}
            handleDisplayName={handleDisplayName}
            handleShowDeleteFileModal={handleShowDeleteFileModal}
          />

          <label for="upload-files">
            <div className="new-upload-container">
              <div className="new-upload-background">
                <div className="new-upload-button">
                  <img src={uploadFileIcon} alt="Icon" width="20" height="20" />
                </div>
              </div>
            </div>
          </label>
        </div>
      )}

      <div className="file-upload-modal-buttons">
        <button
          className="file-upload-modal-button"
          onClick={() => hideModal()}
        >
          Nazad
        </button>

        <button
          className="file-upload-modal-button"
          disabled={files.length === 0}
          onClick={() => onSubmit()}
        >
          Sačuvaj
        </button>
      </div>
    </div>
  );
}
